import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import {map, share, switchMap} from 'rxjs/operators';
import {ConfigService} from '../../core/services/config.service';
import { StaticDictionaryItem } from '../model/StaticDictionaryItem';
import { VatRateDictionaryDataItem } from '../model/VatRateDictionaryDataItem';
import { CacheService } from './cache.service';
import { InvoiceTypeDictionaryDataItem } from  '../model/InvoiceTypeDictionaryDataItem';
import { TranscodingDictionaryDataItem } from '../model/TranscodingDictionaryDataItem';


@Injectable({
  providedIn: 'root'
})
export class StaticDictionaryService {

  localizationCode: string;

  constructor(private http: HttpClient,
              private configService: ConfigService,
              private translateService: TranslateService,
              private cacheService: CacheService
  ) {

  }

  public getStaticDictionary(key: string, cacheUnique = ''): Observable<StaticDictionaryItem[]> {

    this.localizationCode = this.translateService.getDefaultLang();
    const cacheKey = key + '-' + this.localizationCode + cacheUnique;

    const cachedData = this.cacheService.load(cacheKey);
    if(cachedData !== null){
      return of(cachedData);
    }
       
    return this.http.get<StaticDictionaryItem[]>(
        this.configService.PortalWebApiUrl + 'StaticDictionary/' + key + '/' + this.localizationCode)
        .pipe(
          switchMap( r => { this.cacheService.save({key:cacheKey,data:r }); return of<StaticDictionaryItem[]>(r); } )
        );
  }

  public getStaticDictionaryWithAdditionalOption(key: string, optionCaptionKey = 'global.all', optionId = 0): Observable<StaticDictionaryItem[]> {
    this.localizationCode = this.translateService.getDefaultLang();

    const newCaption = this.translateService.instant(optionCaptionKey);
    const newItem = { value: newCaption, id: optionId, key: key}

    return this.getStaticDictionary(key,optionCaptionKey + optionId).pipe(map(r => { r.unshift(newItem); return r;}));
  }


  public getVatRates(): Observable<VatRateDictionaryDataItem[]> {

    this.localizationCode = this.translateService.getDefaultLang();
    const cacheKey = 'vatRates-' + this.localizationCode;

    const cachedData = this.cacheService.load(cacheKey);
    if(cachedData !== null){
      return of(cachedData);
    }

      return this.http.get<VatRateDictionaryDataItem[]>(
        this.configService.PortalWebApiUrl + 'StaticDictionary/VatRates/' +  this.localizationCode)
        .pipe(
          switchMap( r => { this.cacheService.save({key:cacheKey,data:r }); return of<VatRateDictionaryDataItem[]>(r); } )
        );
  }

  public getInvoiceTypes(): Observable<InvoiceTypeDictionaryDataItem[]> {

    this.localizationCode = this.translateService.getDefaultLang();
    const cacheKey = 'invoiceTypes-' + this.localizationCode;

    const cachedData = this.cacheService.load(cacheKey);
    if(cachedData !== null){
      return of(cachedData);
    }

      return this.http.get<InvoiceTypeDictionaryDataItem[]>(
        this.configService.PortalWebApiUrl + 'StaticDictionary/InvoiceTypes/' +  this.localizationCode)
        .pipe(
          switchMap( r => { this.cacheService.save({key:cacheKey,data:r }); return of<InvoiceTypeDictionaryDataItem[]>(r); } )
        );
  }

  public getTranscoding(contextName: string): Observable<TranscodingDictionaryDataItem[]> {

    const cacheKey = 'transcoding-' + contextName;

    const cachedData = this.cacheService.load(cacheKey);
    if(cachedData !== null){
      return of(cachedData);
    }

      return this.http.get<TranscodingDictionaryDataItem[]>(
        this.configService.PortalWebApiUrl + 'StaticDictionary/Transcoding/' +  contextName)
        .pipe(
          switchMap( r => { this.cacheService.save({key:cacheKey,data:r }); return of<TranscodingDictionaryDataItem[]>(r); } )
        );
  }

  public getEmails(pattern:string ): Observable<string[]> {
      if(!pattern){
        return of<string[]>([]);
      }
  
        return this.http.get<string[]>(
          this.configService.PortalWebApiUrl + 'StaticDictionary/email/' + pattern)
          .pipe(
            switchMap( r => {  return of<string[]>(r); } ),
            share({ connector: () => new ReplaySubject(1)}),
          );
    }

}